import * as React from "react";
import { Chart } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { ChartColors, getChartDataLabelFormatter, getTitleOptions, getToolTipFormattedValue, handleLegendHover } from "../chartInitialize";
import { htmlLegendPlugin } from "../HtmlLegendPlugIn";
import { IReportChartDataSet } from "ui/Scripts/source/models/report/reportChartDataSet";
import { ChartValueFormatting } from "ui/Scripts/source/models/enums";
import { Base } from "ui/Scripts/source/framework/base";
import { useEffect } from "react";

export interface IBarChartProp {
    title?: string;
    dataId: string;
    labels: string[];
    yAxisValueFormatting?: ChartValueFormatting;
    dataSets: IReportChartDataSet[];
    legendContainerId: string;
    showDataLabels?: boolean;
}

// type Align = 'bottom' | 'center' | 'end' | 'left' | 'right' | 'start' | 'top' | number;
// type Anchor = 'center' | 'end' | 'start';

const getChartDataSets = (dataSets: IReportChartDataSet[]) => {
    return dataSets.map((i, index) => (
        {
            label: i.label,
            data: i.data,
            fill: "none",
            pointRadius: 2,
            borderWidth: 0,
            lineTension: 0,
            backgroundColor: ChartColors[index % ChartColors.length],
            borderColor: ChartColors[index % ChartColors.length],
            color: "#333",
            datalabels: {
                anchor: "end",
                align: "top",
                overlap: "auto",
                display: () => {
                    return i.showDataLabels;
                },
                formatter: (value, context) => getChartDataLabelFormatter(value, context, dataSets)
            }
        }
    ));
};

export const BarChart = (props: IBarChartProp) => {
    const canvasRef = React.useRef<HTMLCanvasElement>(null);
    const chartRef = React.useRef<Chart | null>(null);

    const getTooltipLabel = (tooltipItem) => {
        if (tooltipItem.datasetIndex < 0 || tooltipItem.datasetIndex >= props.dataSets.length) return tooltipItem.label;
        const chartTooltipFormat = props.dataSets[tooltipItem.datasetIndex].chartValueFormatting;
        return `${tooltipItem.dataset.label || ""}: ${getToolTipFormattedValue(chartTooltipFormat, tooltipItem.raw, tooltipItem.formattedValue)}`;
    };

    const afterTickToLabelConversion = (axis) => {
        const yAxisValueFormatting = props.yAxisValueFormatting || ChartValueFormatting.None;
        if (yAxisValueFormatting === ChartValueFormatting.TimeSpan) {
            axis.ticks.forEach((tick) => { tick.label = Base.timeSpanToString(Number(tick.value)); });
        }
    };

    const createChart = () => {
        const hasDataLabels = props.dataSets.some(ds => ds.showDataLabels);
        chartRef.current = new Chart(canvasRef.current, {
            type: "bar",
            options: {
                locale: appConfig.culture,
                maintainAspectRatio: false,
                layout: {
                    padding: {
                        top: hasDataLabels ? 30 : 0
                    }
                },
                plugins: {
                    //    colorschemes: {
                    //        scheme: 'brewer.Paired12'
                    //    }
                    title: getTitleOptions(props.title),
                    tooltip: {
                        boxPadding: 3,
                        callbacks: {
                            label: getTooltipLabel
                        }
                    },
                    legend: {
                        display: !props.legendContainerId,
                        position: "right",
                        maxWidth: 200,
                        onHover: (evt, legendItem) => handleLegendHover(evt, legendItem, chartRef.current)
                    },
                    // @ts-ignore
                    htmlLegend: {
                        // ID of the container to put the legend in
                        containerID: props.legendContainerId,
                    }
                },
                scales: {
                    x: {
                        ticks: {
                            font: {
                                size: 10
                            },
                            autoSkip: false,
                            maxRotation: 90,
                            minRotation: 90
                        },
                    },
                    y: {
                        ticks: {
                            font: {
                                size: 10
                            },
                            precision: ((props.yAxisValueFormatting || ChartValueFormatting.None) === ChartValueFormatting.Integer)
                                ? 0
                                : undefined,
                            //,min: 0
                        },
                        afterTickToLabelConversion
                    }
                },
            },
            data: {
                labels: props.labels.slice(0),
                datasets: getChartDataSets(props.dataSets) as any
            },
            plugins: props.legendContainerId
                ? [htmlLegendPlugin, ChartDataLabels]
                : [ChartDataLabels]
        });
    };

    useEffect(() => {
        createChart();

        return () => {
            if (chartRef.current) {
                chartRef.current.destroy();
            }
        };
    }, []);

    useEffect(() => {
        const chart = chartRef.current;
        if (!chart) return;
        const hasDataLabels = props.dataSets.some(ds => ds.showDataLabels);
        chart.config.options.layout.padding = {
            top: hasDataLabels ? 30 : 0
        };
        chart.config.options.plugins.title.text = props.title;
        chart.config.options.plugins.tooltip.callbacks.label = getTooltipLabel;
        chart.config.options.plugins.datalabels = {
            anchor: "end",
            align: "top",
            display: () => {
                return !!props.showDataLabels;
            }
        };
        chart.data.labels = props.labels.slice(0);
        chart.data.datasets = getChartDataSets(props.dataSets) as any;
        (chart.config.options.scales.y.ticks as any).precision = ((props.yAxisValueFormatting || ChartValueFormatting.None) === ChartValueFormatting.Integer)
            ? 0
            : undefined;
        chart.config.options.scales.y.afterTickToLabelConversion = afterTickToLabelConversion;
        chart.update();
    }, [props.dataId]);

    return (
        <canvas ref={canvasRef} />
    );
};